/**
 * Left panel of the Pipeline tab: the stage checklist / live stage tree.
 *
 * It also carries the Run action. That used to live only in the toolbar, so a
 * session whose toolbar was hidden offered a folder, a list of steps, and no
 * way to start — the checklist was the whole panel. A protocol you can choose
 * steps in should be a protocol you can run from, and the footer is where the
 * eye lands after ticking the last box.
 *
 * Each step also says how many parameters it has, because the parameters are on
 * the other side of the window and nothing here pointed at them.
 */

import { Play, Square } from "lucide-react";

import { Button, Checkbox, EmptyState, Panel } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { stageParamCount } from "@/pipeline/params";
import { usePipelineSession } from "@/pipeline/session";
import { dependencyHint, modalitiesFor, stageKey, stagesFor } from "@/pipeline/stages";
import { STAGE_LABEL, StageGlyph } from "@/pipeline/status";
import type { StageId } from "@/ipc/contract";
import { useJob, useJobRunning } from "@/store/appStore";

export function PipelineBrowser() {
  const draft = usePipelineSession((s) => s.draft);
  const mode = usePipelineSession((s) => s.mode);
  const selected = usePipelineSession((s) => s.selectedStageKey);
  const toggleStage = usePipelineSession((s) => s.toggleStage);
  const selectStage = usePipelineSession((s) => s.selectStage);
  const job = useJob();

  const modalities = modalitiesFor(draft.pipelineKind);

  return (
    <Panel title="Protocol steps" className="h-full">
      {modalities.length === 0 ? (
        <EmptyState title="No stages" />
      ) : (
        <div className="py-1 text-xs">
          {modalities.map((modality) => (
            <div key={modality} className="mb-2">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                {modality}
              </div>
              {stagesFor(modality).map((meta) => {
                const key = stageKey(modality, meta.id);
                const selectedIds = modality === "DWI" ? draft.stages.dwi : draft.stages.pwi;
                const checked = selectedIds.includes(meta.id);
                const live = job?.stages.find((s) => s.modality === modality && s.id === meta.id);
                const hint = dependencyHint(modality, meta.id);
                const params = stageParamCount(modality, meta.id as StageId);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => selectStage(key)}
                    className={cn(
                      "flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-neutral-50",
                      selected === key && "bg-ads-accent-soft",
                    )}
                  >
                    {mode === "running" && live ? (
                      <StageGlyph state={live.state} className="mt-0.5" />
                    ) : (
                      <Checkbox
                        className="mt-0.5"
                        checked={checked}
                        onChange={() => toggleStage(modality, meta.id as StageId)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-neutral-800">{meta.label}</span>
                        {params > 0 ? (
                          <span
                            className="shrink-0 text-[10px] tabular-nums text-neutral-400"
                            title="Parameters for this step, shown on the right when it is selected"
                          >
                            {params} params
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-[11px] text-neutral-400">
                        {mode === "running" && live ? STAGE_LABEL[live.state] : hint ?? meta.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          <RunFooter />
        </div>
      )}
    </Panel>
  );
}

/**
 * Sticky, because the point is that it is visible without hunting. `run()` does
 * its own checking and writes the reason into the notice strip, so the button
 * stays enabled for everything except a count of zero — a button that greys out
 * without saying why is the thing this panel was missing in the first place.
 */
function RunFooter() {
  const draft = usePipelineSession((s) => s.draft);
  const busy = usePipelineSession((s) => s.busy);
  const dataMode = usePipelineSession((s) => s.dataMode);
  const selectedSubjectIds = usePipelineSession((s) => s.selectedSubjectIds);
  const run = usePipelineSession((s) => s.run);
  const stop = usePipelineSession((s) => s.stop);
  const running = useJobRunning();

  const steps = draft.stages.dwi.length + draft.stages.pwi.length;
  const subjects = dataMode === "dataset" ? selectedSubjectIds.length : 1;

  return (
    <div className="sticky bottom-0 mt-1 border-t border-ads-border bg-ads-surface px-3 py-2">
      {running ? (
        <Button size="sm" variant="danger" className="w-full justify-center" onClick={() => void stop()}>
          <Square className="h-3 w-3" />
          Stop
        </Button>
      ) : (
        <Button
          size="sm"
          variant="primary"
          className="w-full justify-center"
          disabled={busy || steps === 0}
          onClick={() => void run()}
        >
          <Play className="h-3 w-3" />
          {steps === 0 ? "No steps selected" : `Run ${steps} ${steps === 1 ? "step" : "steps"}`}
        </Button>
      )}
      <p className="mt-1 text-center text-[10px] text-neutral-400">
        {running
          ? "Progress is above the workspace, on every page."
          : dataMode === "dataset"
            ? `${subjects} ${subjects === 1 ? "subject" : "subjects"} checked · runs one after another`
            : draft.subjectDir
              ? draft.subjectId || "this subject"
              : "Choose a subject folder first"}
      </p>
    </div>
  );
}
